import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Table, Typography, Badge, Spin, 
  Row, Col, Statistic, Tabs, Tag, List, Space, Tooltip,
  Empty
} from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CarOutlined, ClockCircleOutlined, WarningOutlined,
  RiseOutlined, FallOutlined, CloseOutlined
} from '@ant-design/icons';
import OTRTripChart from './OTRTripChart';
import {toHrsMinsSecs} from '../../../libs/formatLib';
import { OTR_THRESHOLDS } from '../utils/OTRDataService';
import otrDataProcessor from '../utils/OTRDataProcessor';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const OTRTripDetailView = ({ tripId, dateId, onClose, tripStartTimes, preloadedData, route }) => {
  const [loading, setLoading] = useState(false);
  const [tripData, setTripData] = useState(null);
  const [stopRecords, setStopRecords] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [tripStats, setTripStats] = useState({
    onTimePercentage: 0,
    earlyPercentage: 0,
    latePercentage: 0,
    averageVariance: 0
  });

  // Process data when component mounts or when props change
  useEffect(() => {
    if(tripId && dateId && preloadedData) {
      processTripData();
    }
  }, [tripId, dateId, preloadedData]);

  // Helper function to try to parse and fix time data
  const repairTimeData = (timeValue) => {
    // If we already have a valid number, return it
    if (typeof timeValue === 'number' && !isNaN(timeValue)) {
      return timeValue;
    }
    
    // If it's undefined/null, return null
    if (timeValue === undefined || timeValue === null) {
      return null;
    }
    
    // Try to convert string to number
    if (typeof timeValue === 'string') {
      // Check if it's a time string like "HH:MM:SS"
      if (timeValue.includes(':')) {
        try {
          const [hours, minutes, seconds] = timeValue.split(':').map(Number);
          return hours * 60 + minutes + (seconds / 60);
        } catch (e) {
          console.warn(`Failed to parse time string: ${timeValue}`, e);
          return null;
        }
      }
      
      // Try direct conversion to number
      const parsed = Number(timeValue);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    
    // Return null for anything we can't handle
    return null;
  };

  const processTripData = () => {
    setLoading(true);
    try {
      // Use the preloaded data instead of fetching from storage
      if (!preloadedData || preloadedData.length === 0) {
        throw new Error(`No data provided for trip ${tripId}${dateId ? ` on date ${dateId}` : ''}`);
      }
      
      // Filter for this specific trip
      let tripStops = preloadedData.filter(record => (record.KEY || record.TRIP_ID) === tripId);
      
      // If dateId is provided, further filter by date
      if (dateId) {
        tripStops = tripStops.filter(record => record.DATE_ID == dateId);
        console.log(`Filtered to ${tripStops.length} stops for date ${dateId}`);
      }
      
      if (!tripStops || tripStops.length === 0) {
        throw new Error(`No data found for trip ${tripId}${dateId ? ` on date ${dateId}` : ''}`);
      }

      // Debug check of first stop in this trip
      const firstStop = tripStops[0];
      console.log("First stop in trip - all fields:", firstStop);
      console.log("Trip time fields availability:", {
        hasStandardTimeFields: !!(firstStop.SCHEDULED_ARRIVAL_TIME || firstStop.SCHEDULED_DEPARTURE_TIME || 
                              firstStop.ACTUAL_ARRIVAL_TIME || firstStop.ACTUAL_DEPARTURE_TIME),
        hasCompactTimeFields: !!(firstStop.S_ARR || firstStop.S_DEP || firstStop.A_ARR || firstStop.A_DEP),
        timeFieldValues: {
          SCHEDULED_ARRIVAL_TIME: firstStop.SCHEDULED_ARRIVAL_TIME,
          SCHEDULED_DEPARTURE_TIME: firstStop.SCHEDULED_DEPARTURE_TIME,
          ACTUAL_ARRIVAL_TIME: firstStop.ACTUAL_ARRIVAL_TIME,
          ACTUAL_DEPARTURE_TIME: firstStop.ACTUAL_DEPARTURE_TIME,
          S_ARR: firstStop.S_ARR,
          S_DEP: firstStop.S_DEP,
          A_ARR: firstStop.A_ARR,
          A_DEP: firstStop.A_DEP
        }
      });
      
      // Check all TSN values to see if there's a pattern
      const tsnValues = tripStops.map(stop => stop.TSN);
      console.log("All TSN values in this trip:", tsnValues);
      
      // Log any TSN pattern issues - ensure values are strings before using string methods
      const hasMissingTSN = tsnValues.some(tsn => !tsn);
      const hasPrefix = tsnValues.some(tsn => tsn && String(tsn).startsWith('ST-'));
      
      if (hasMissingTSN) {
        console.warn("Some stops are missing TSN values!");
      }
      
      if (hasPrefix) {
        console.warn("Some TSN values have 'ST-' prefix which might be incorrect!");
      }
      
      // Extract basic trip info from first record
      const basicInfo = {
        tripId: tripId,
        route: route || tripStops[0].ROUTE,
        date: tripStops[0].DATE_ID,
        dateFormatted: tripStops[0].DATE_FORMATTED,
        driverId: tripStops[0].DRIVER_ID,
        depot: tripStops[0].DEPOT,
        operatorId: tripStops[0].OPERATOR_ID,
        contract: tripStops[0].CONTRACT,
        vehicleId: tripStops[0].VEHICLE_ID,
        vehicleShift: tripStops[0].VEHICLE_SHIFT,
        driverShift: tripStops[0].DRIVER_SHIFT
      };

      // Log how many stops we found for this trip
      console.log(`Found ${tripStops.length} stops for Trip: ${tripId}${dateId ? ` on date ${dateId}` : ''}`);

      // Use raw data without any processing that might lose TSN values
      const processedStops = tripStops.map(stop => {
        // Convert numeric TSN values to strings to avoid type errors
        let tsnValue = stop.TSN;
        if (tsnValue !== undefined && tsnValue !== null) {
          tsnValue = String(tsnValue);
        }
        
        // Debug this particular stop to see what time data is available
        console.log(`Processing stop ${stop.TSN}, time data:`, {
          standardFormat: {
            SCHEDULED_ARRIVAL_TIME: stop.SCHEDULED_ARRIVAL_TIME,
            SCHEDULED_DEPARTURE_TIME: stop.SCHEDULED_DEPARTURE_TIME,
            ACTUAL_ARRIVAL_TIME: stop.ACTUAL_ARRIVAL_TIME,
            ACTUAL_DEPARTURE_TIME: stop.ACTUAL_DEPARTURE_TIME
          },
          compactFormat: {
            S_ARR: stop.S_ARR,
            S_DEP: stop.S_DEP,
            A_ARR: stop.A_ARR,
            A_DEP: stop.A_DEP
          },
          hasStandardTimeData: !!(stop.SCHEDULED_ARRIVAL_TIME || stop.SCHEDULED_DEPARTURE_TIME || 
                              stop.ACTUAL_ARRIVAL_TIME || stop.ACTUAL_DEPARTURE_TIME),
          hasCompactTimeData: !!(stop.S_ARR || stop.S_DEP || stop.A_ARR || stop.A_DEP),
        });
        
        // Fix for compact storage format used by OTRDataService
        // Map the compact field names to the full names if needed
        const fixedStop = {
          ...stop,
          // Ensure TSN is preserved exactly as is, but as a string
          TSN: tsnValue,
          TIME_RECORD_SEQUENCE: stop.TIME_RECORD_SEQUENCE,
          TSN_FIRST_LAST_IND: stop.TSN_FIRST_LAST_IND,
        };
        
        // Check various time field formats and populate consistently
        // This handles both TPT imported data and compact storage format
        
        // Fix scheduled arrival time
        if (stop.S_ARR !== undefined) {
          fixedStop.SCHEDULED_ARRIVAL_TIME = stop.S_ARR;
        } else if (stop.SCHEDULED_ARRIVAL_TIME) {
          // If standard field contains date-time string, convert to minutes past midnight
          fixedStop.SCHEDULED_ARRIVAL_TIME = repairTimeData(stop.SCHEDULED_ARRIVAL_TIME);
        } 
        
        // Fix scheduled departure time
        if (stop.S_DEP !== undefined) {
          fixedStop.SCHEDULED_DEPARTURE_TIME = stop.S_DEP;
        } else if (stop.SCHEDULED_DEPARTURE_TIME) {
          fixedStop.SCHEDULED_DEPARTURE_TIME = repairTimeData(stop.SCHEDULED_DEPARTURE_TIME);
        }
        
        // Fix actual arrival time
        if (stop.A_ARR !== undefined) {
          fixedStop.ACTUAL_ARRIVAL_TIME = stop.A_ARR;
        } else if (stop.ACTUAL_ARRIVAL_TIME) {
          fixedStop.ACTUAL_ARRIVAL_TIME = repairTimeData(stop.ACTUAL_ARRIVAL_TIME);
        }
        
        // Fix actual departure time
        if (stop.A_DEP !== undefined) {
          fixedStop.ACTUAL_DEPARTURE_TIME = stop.A_DEP;
        } else if (stop.ACTUAL_DEPARTURE_TIME) {
          fixedStop.ACTUAL_DEPARTURE_TIME = repairTimeData(stop.ACTUAL_DEPARTURE_TIME);
        }
        
        return fixedStop;
      });
      
      // Determine stop sequence if not available
      const sortedStops = determineStopSequence(processedStops);
      
      // Set state
      setTripData(basicInfo);
      setStopRecords(sortedStops);
      setChartData(prepareChartData(sortedStops));
      
      // Use centralized processor for trip statistics calculation
      const stats = otrDataProcessor.calculateStopStats(sortedStops, false);
      setTripStats({
        onTimePercentage: stats.onTimePercent,
        earlyPercentage: stats.earlyPercent,
        latePercentage: stats.latePercent,
        averageVariance: stats.avgVariance
      });
      
    } catch (error) {
      console.error('Error processing trip data:', error);
      setTripData(null);
      setStopRecords([]);
      setChartData([]);
      setTripStats({
        onTimePercentage: 0,
        earlyPercentage: 0,
        latePercentage: 0,
        averageVariance: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const determineStopSequence = (stops) => {
    // First check for TIME_RECORD_SEQUENCE field (from TPT file)
    const hasTimeRecordSequence = stops.some(stop => stop.TIME_RECORD_SEQUENCE !== undefined);
    
    if (hasTimeRecordSequence) {
      console.log("Using TIME_RECORD_SEQUENCE from TPT file");
      return [...stops].sort((a, b) => {
        // Convert to number in case it's stored as string
        const seqA = Number(a.TIME_RECORD_SEQUENCE || 0);
        const seqB = Number(b.TIME_RECORD_SEQUENCE || 0);
        return seqA - seqB;
      });
    }
    
    // Next try to use any existing sequence data
    const hasSequence = stops.some(stop => stop.SEQUENCE_NUMBER !== undefined);
    
    if (hasSequence) {
      console.log("Using existing SEQUENCE_NUMBER");
      return [...stops].sort((a, b) => {
        const seqA = Number(a.SEQUENCE_NUMBER || 0);
        const seqB = Number(b.SEQUENCE_NUMBER || 0);
        return seqA - seqB;
      });
    }
    
    // If we have STOP_SEQUENCE field (another common field in transit data)
    const hasStopSequence = stops.some(stop => stop.STOP_SEQUENCE !== undefined);
    
    if (hasStopSequence) {
      console.log("Using STOP_SEQUENCE field");
      return [...stops].sort((a, b) => {
        const seqA = Number(a.STOP_SEQUENCE || 0);
        const seqB = Number(b.STOP_SEQUENCE || 0);
        return seqA - seqB;
      });
    }
    
    console.log("Inferring sequence from stop types and times");
    // Infer sequence based on first/mid/last indicators and scheduled times
    let firstStops = stops.filter(stop => stop.TSN_FIRST_LAST_IND === 'F');
    let midStops = stops.filter(stop => stop.TSN_FIRST_LAST_IND === 'M');
    let lastStops = stops.filter(stop => stop.TSN_FIRST_LAST_IND === 'L');
    
    // Sort first, mid, and last stops by scheduled time if available
    if (firstStops.length > 0) {
      firstStops = sortStopsByTime(firstStops);
    }
    
    if (midStops.length > 0) {
      midStops = sortStopsByTime(midStops);
    }
    
    if (lastStops.length > 0) {
      lastStops = sortStopsByTime(lastStops);
    }
    
    // Combine and add sequence numbers
    const sortedStops = [...firstStops, ...midStops, ...lastStops].map((stop, index) => ({
      ...stop,
      SEQUENCE_NUMBER: index + 1
    }));
    
    return sortedStops;
  };

  // Helper to sort stops by scheduled time
  const sortStopsByTime = (stops) => {
    return [...stops].sort((a, b) => {
      // Try different time fields in order of preference
      const timeA = a.SCHEDULED_ARRIVAL_TIME || a.SCHEDULED_DEPARTURE_TIME || 0;
      const timeB = b.SCHEDULED_ARRIVAL_TIME || b.SCHEDULED_DEPARTURE_TIME || 0;
      return timeA - timeB;
    });
  };

  const prepareChartData = (stops) => {
    return stops.map((stop, index) => {
      // Use centralized processor for variance calculation
      const variance = otrDataProcessor.getDepartureVariance(stop);
      
      return {
        name: `Stop ${index + 1}`,
        stopId: stop.TSN || '',  // Use raw TSN value without modification
        variance: variance,
        sequence: stop.TIME_RECORD_SEQUENCE !== undefined ? stop.TIME_RECORD_SEQUENCE : (index + 1),
        scheduled: stop.SCHEDULED_DEPARTURE_TIME || stop.SCHEDULED_ARRIVAL_TIME,
        actual: stop.ACTUAL_DEPARTURE_TIME || stop.ACTUAL_ARRIVAL_TIME,
        status: otrDataProcessor.getOnTimeStatus(variance, stop, false)
      };
    });
  };

  // Helper function to explicitly parse datetime strings into minutes past midnight
  const parseTimeString = (timeString) => {
    if (!timeString) return null;
    
    try {
      // Check if it's already a number (minutes past midnight)
      if (typeof timeString === 'number' && !isNaN(timeString)) {
        return timeString;
      }
      
      // Parse date-time string format like "2025-04-02 14:10:00" or "2025-05-09 14:46:40"
      if (typeof timeString === 'string') {
        // Extract the time part if it includes a date
        let timePart = timeString;
        if (timeString.includes(' ')) {
          const parts = timeString.split(' ');
          if (parts.length >= 2) {
            timePart = parts[1];
          }
        }
        
        // Parse HH:MM:SS format
        if (timePart && timePart.includes(':')) {
          const parts = timePart.split(':');
          if (parts.length >= 2) {
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            // Ensure we capture seconds precisely - default to 0 if not provided
            const seconds = parts.length >= 3 ? parseInt(parts[2], 10) : 0;
            
            if (!isNaN(hours) && !isNaN(minutes)) {
              // Convert to minutes past midnight, preserving seconds exactly
              // e.g., "14:46:40" becomes 14*60 + 46 + (40/60) = 886.6666...
              console.log(`Parsing ${timePart} to minutes: ${hours}h ${minutes}m ${seconds}s = ${hours * 60 + minutes + (seconds / 60)}`);
              return hours * 60 + minutes + (seconds / 60);
            }
          }
        }
      }
      
      console.warn('Failed to parse time string:', timeString);
      return null;
    } catch (error) {
      console.warn('Error parsing time string:', timeString, error);
      return null;
    }
  };

  // Helper function to convert minutes since midnight to HH:MM:SS format
  const minutesToTimeString = (minutes) => {
    // Add detailed debugging for time data issues
    if (minutes === undefined || minutes === null || isNaN(minutes)) {
      console.log(`Invalid time value: ${minutes}, type: ${typeof minutes}`);
      return '-';
    }
    
    try {
      // Convert to number explicitly to handle string inputs
      const minutesNum = Number(minutes);
      if (isNaN(minutesNum)) {
        console.log(`Time conversion failed - NaN result: ${minutes}`);
        return '-';
      }
      
      // Handle potential negative time values (could happen with overnight trips)
      const adjustedMinutes = minutesNum < 0 ? minutesNum + (24 * 60) : minutesNum;
      
      // Calculate hours and minutes without rounding
      const hours = Math.floor(adjustedMinutes / 60) % 24;
      const mins = Math.floor(adjustedMinutes % 60);
      
      // Calculate seconds from the fractional part of minutes
      // Use Math.floor to avoid rounding up - we want to preserve exact seconds
      const fractionalMinutes = adjustedMinutes - Math.floor(adjustedMinutes);
      const secs = Math.floor(fractionalMinutes * 60);
      
      // Additional validations to prevent NaN in output
      if (isNaN(hours) || isNaN(mins) || isNaN(secs)) {
        console.log(`Time parts invalid: h=${hours}, m=${mins}, s=${secs} from input=${minutes}`);
        return '-';
      }
      
      // Format with leading zeros for consistent display
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    } catch (error) {
      console.warn('Error formatting time:', minutes, error);
      return '-';
    }
  };

  // Columns for stop data table
  const columns = [
    {
      title: '#',
      dataIndex: 'SEQUENCE_NUMBER',
      key: 'sequence',
      width: 60,
      render: (seq, record) => {
        const sequence = seq || record.TIME_RECORD_SEQUENCE || '';
        return <Text>{sequence}</Text>;
      }
    },
    {
      title: 'TSN',
      dataIndex: 'TSN',
      key: 'tsn',
      width: 100,
      render: (tsn, record) => {
        // Make sure we convert any numeric TSN to string
        let tsnValue = tsn;
        if (tsnValue !== undefined && tsnValue !== null) {
          // Convert to string if it's a number
          tsnValue = String(tsnValue);
        }
        
        // Enhanced debugging for TSN values
        console.log(`Rendering TSN cell: value=${tsnValue}, type=${typeof tsn}`);
        
        // Try to get TSN from multiple possible sources if primary is missing
        if (!tsnValue) {
          tsnValue = record.stopId || record.STOP_ID || record.STOP_CODE || '';
        }
        
        return <Text>{tsnValue}</Text>;
      }
    },
    {
      title: 'Stop Type',
      dataIndex: 'TSN_FIRST_LAST_IND',
      key: 'stopType',
      width: 100,
      render: (type) => {
        // If type is null or undefined, don't display anything
        if (type === null || type === undefined || type === '') {
          return '';
        }
        
        let text = '';
        let color = '';
        
        if (type === 'F') {
          text = 'First';
          color = 'green';
        } else if (type === 'M') {
          text = 'Mid';
          color = 'blue';
        } else if (type === 'L') {
          text = 'Last';
          color = 'orange';
        }
        
        return text ? <Tag color={color}>{text}</Tag> : '';
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => {
        // Prioritize departure variance
        let actualVariance = null;
        
        // First check for departure variance
        if (record.DEPARTURE_TIME_VARIANCE !== undefined && record.DEPARTURE_TIME_VARIANCE !== null) {
          actualVariance = record.DEPARTURE_TIME_VARIANCE;
        } 
        // Then try to calculate from compact departure time fields
        else if (record.A_DEP !== undefined && record.S_DEP !== undefined) {
          actualVariance = record.A_DEP - record.S_DEP;
        }
        // Then try to calculate from departure date-time strings
        else if (record.ACTUAL_DEPARTURE_TIME && record.SCHEDULED_DEPARTURE_TIME) {
          const actualMinutes = parseTimeString(record.ACTUAL_DEPARTURE_TIME);
          const scheduledMinutes = parseTimeString(record.SCHEDULED_DEPARTURE_TIME);
          
          if (actualMinutes !== null && scheduledMinutes !== null) {
            actualVariance = actualMinutes - scheduledMinutes;
          }
        }
        
        // Fall back to arrival variance only if departure variance is not available
        if (actualVariance === null || actualVariance === undefined || isNaN(Number(actualVariance))) {
          if (record.ARRIVAL_TIME_VARIANCE !== undefined && record.ARRIVAL_TIME_VARIANCE !== null) {
            actualVariance = record.ARRIVAL_TIME_VARIANCE;
          }
          else if (record.A_ARR !== undefined && record.S_ARR !== undefined) {
            actualVariance = record.A_ARR - record.S_ARR;
          }
          else if (record.ACTUAL_ARRIVAL_TIME && record.SCHEDULED_ARRIVAL_TIME) {
            const actualMinutes = parseTimeString(record.ACTUAL_ARRIVAL_TIME);
            const scheduledMinutes = parseTimeString(record.SCHEDULED_ARRIVAL_TIME);
            
            if (actualMinutes !== null && scheduledMinutes !== null) {
              actualVariance = actualMinutes - scheduledMinutes;
            }
          }
        }
          
        if (actualVariance === undefined || actualVariance === null) {
          return <Badge status="default" text="Unknown" />;
        }
        
        let status, text, icon;
        
        // Update OTR rules: On time is between 2 minutes early and 5 minutes late
        if (actualVariance <= OTR_THRESHOLDS.early) {
          status = 'error';  // red
          text = 'Early';
          icon = <RiseOutlined style={{ color: 'red' }} />;
        } else if (actualVariance >= OTR_THRESHOLDS.late) {
          status = 'processing';  // blue
          text = 'Late';
          icon = <FallOutlined style={{ color: 'blue' }} />;
        } else {
          status = 'success';  // green
          text = 'On Time';
          icon = <CheckCircleOutlined style={{ color: 'green' }} />;
        }
        
        return (
          <Space>
            <Badge status={status} />
            {icon}
            <Text>{text}</Text>
          </Space>
        );
      }
    },
    {
      title: 'Scheduled Departure',
      key: 'scheduled',
      width: 120,
      render: (_, record) => {
        try {
          // Debug the available time data
          console.log(`Rendering scheduled time for TSN ${record.TSN}:`, {
            SCHEDULED_ARRIVAL_TIME: record.SCHEDULED_ARRIVAL_TIME,
            SCHEDULED_DEPARTURE_TIME: record.SCHEDULED_DEPARTURE_TIME,
            S_ARR: record.S_ARR,
            S_DEP: record.S_DEP,
            S_ARR_STR: record.S_ARR_STR,
            S_DEP_STR: record.S_DEP_STR,
            SCHEDULED_ARRIVAL_TIME_STRING: record.SCHEDULED_ARRIVAL_TIME_STRING,
            SCHEDULED_DEPARTURE_TIME_STRING: record.SCHEDULED_DEPARTURE_TIME_STRING,
            type: {
              SCHEDULED_ARRIVAL_TIME: typeof record.SCHEDULED_ARRIVAL_TIME,
              SCHEDULED_DEPARTURE_TIME: typeof record.SCHEDULED_DEPARTURE_TIME,
              S_ARR: typeof record.S_ARR,
              S_DEP: typeof record.S_DEP
            }
          });
          
          // Prioritize departure time fields
          
          // First check for the original time string from compact storage
          if (record.S_DEP_STR) {
            // Extract time part from date-time string if needed
            const timePart = record.S_DEP_STR.includes(' ') ? 
              record.S_DEP_STR.split(' ')[1] : record.S_DEP_STR;
            console.log(`Using S_DEP_STR: ${record.S_DEP_STR} → ${timePart}`);
            return <Text>{timePart}</Text>;
          }
          
          // Then check if we have original datetime strings available
          if (record.SCHEDULED_DEPARTURE_TIME_STRING && typeof record.SCHEDULED_DEPARTURE_TIME_STRING === 'string') {
            // Extract time part from date-time string
            const timePart = record.SCHEDULED_DEPARTURE_TIME_STRING.split(' ')[1];
            if (timePart) {
              console.log(`Using direct time from SCHEDULED_DEPARTURE_TIME_STRING: ${timePart}`);
              return <Text>{timePart}</Text>;
            }
          }
          
          // Then try compact format fields (already in minutes past midnight)
          let minutes = null;
          
          if (record.S_DEP !== undefined && record.S_DEP !== null) {
            minutes = Number(record.S_DEP);
            console.log(`Using S_DEP: ${record.S_DEP} → ${minutes}`);
          } 
          // Then try to parse date-time string format fields
          else if (record.SCHEDULED_DEPARTURE_TIME) {
            minutes = parseTimeString(record.SCHEDULED_DEPARTURE_TIME);
            console.log(`Using SCHEDULED_DEPARTURE_TIME: ${record.SCHEDULED_DEPARTURE_TIME} → ${minutes}`);
          }
          
          // Fall back to arrival times only if departure times are not available
          if (minutes === null || isNaN(minutes)) {
            if (record.S_ARR_STR) {
              const timePart = record.S_ARR_STR.includes(' ') ? 
                record.S_ARR_STR.split(' ')[1] : record.S_ARR_STR;
              console.log(`Falling back to S_ARR_STR: ${record.S_ARR_STR} → ${timePart}`);
              return <Text>{timePart}</Text>;
            }
            
            if (record.SCHEDULED_ARRIVAL_TIME_STRING && typeof record.SCHEDULED_ARRIVAL_TIME_STRING === 'string') {
              const timePart = record.SCHEDULED_ARRIVAL_TIME_STRING.split(' ')[1];
              if (timePart) {
                console.log(`Falling back to SCHEDULED_ARRIVAL_TIME_STRING: ${timePart}`);
                return <Text>{timePart}</Text>;
              }
            }
            
            if (record.S_ARR !== undefined && record.S_ARR !== null) {
              minutes = Number(record.S_ARR);
              console.log(`Falling back to S_ARR: ${record.S_ARR} → ${minutes}`);
            } else if (record.SCHEDULED_ARRIVAL_TIME) {
              minutes = parseTimeString(record.SCHEDULED_ARRIVAL_TIME);
              console.log(`Falling back to SCHEDULED_ARRIVAL_TIME: ${record.SCHEDULED_ARRIVAL_TIME} → ${minutes}`);
            }
          }
          
          if (minutes === null || isNaN(minutes)) {
            return <Text>-</Text>;
          }
          
          const timeString = minutesToTimeString(minutes);
          console.log(`Time conversion: ${minutes} → ${timeString}`);
          return <Text>{timeString}</Text>;
        } catch (error) {
          console.error('Error rendering scheduled time:', error);
          return <Text>-</Text>;
        }
      }
    },
    {
      title: 'Actual Departure',
      key: 'actual',
      width: 120,
      render: (_, record) => {
        try {
          // Debug the available time data
          console.log(`Rendering actual time for TSN ${record.TSN}:`, {
            ACTUAL_ARRIVAL_TIME: record.ACTUAL_ARRIVAL_TIME,
            ACTUAL_DEPARTURE_TIME: record.ACTUAL_DEPARTURE_TIME,
            A_ARR: record.A_ARR,
            A_DEP: record.A_DEP,
            A_ARR_STR: record.A_ARR_STR,
            A_DEP_STR: record.A_DEP_STR,
            ACTUAL_ARRIVAL_TIME_STRING: record.ACTUAL_ARRIVAL_TIME_STRING,
            ACTUAL_DEPARTURE_TIME_STRING: record.ACTUAL_DEPARTURE_TIME_STRING,
            type: {
              ACTUAL_ARRIVAL_TIME: typeof record.ACTUAL_ARRIVAL_TIME,
              ACTUAL_DEPARTURE_TIME: typeof record.ACTUAL_DEPARTURE_TIME,
              A_ARR: typeof record.A_ARR,
              A_DEP: typeof record.A_DEP
            }
          });
          
          // Prioritize departure time fields
          
          // First check for the original time string from compact storage
          if (record.A_DEP_STR) {
            // Extract time part from date-time string if needed
            const timePart = record.A_DEP_STR.includes(' ') ? 
              record.A_DEP_STR.split(' ')[1] : record.A_DEP_STR;
            console.log(`Using A_DEP_STR: ${record.A_DEP_STR} → ${timePart}`);
            return <Text>{timePart}</Text>;
          }
          
          // Then check if we have original datetime strings available
          if (record.ACTUAL_DEPARTURE_TIME_STRING && typeof record.ACTUAL_DEPARTURE_TIME_STRING === 'string') {
            // Extract time part from date-time string
            const timePart = record.ACTUAL_DEPARTURE_TIME_STRING.split(' ')[1];
            if (timePart) {
              console.log(`Using direct time from ACTUAL_DEPARTURE_TIME_STRING: ${timePart}`);
              return <Text>{timePart}</Text>;
            }
          }
          
          // Then try compact format fields (already in minutes past midnight)
          let minutes = null;
          
          if (record.A_DEP !== undefined && record.A_DEP !== null) {
            minutes = Number(record.A_DEP);
            console.log(`Using A_DEP: ${record.A_DEP} → ${minutes}`);
          }
          // Then try to parse date-time string format fields
          else if (record.ACTUAL_DEPARTURE_TIME) {
            minutes = parseTimeString(record.ACTUAL_DEPARTURE_TIME);
            console.log(`Using ACTUAL_DEPARTURE_TIME: ${record.ACTUAL_DEPARTURE_TIME} → ${minutes}`);
          }
          
          // Fall back to arrival times only if departure times are not available
          if (minutes === null || isNaN(minutes)) {
            if (record.A_ARR_STR) {
              const timePart = record.A_ARR_STR.includes(' ') ? 
                record.A_ARR_STR.split(' ')[1] : record.A_ARR_STR;
              console.log(`Falling back to A_ARR_STR: ${record.A_ARR_STR} → ${timePart}`);
              return <Text>{timePart}</Text>;
            }
            
            if (record.ACTUAL_ARRIVAL_TIME_STRING && typeof record.ACTUAL_ARRIVAL_TIME_STRING === 'string') {
              const timePart = record.ACTUAL_ARRIVAL_TIME_STRING.split(' ')[1];
              if (timePart) {
                console.log(`Falling back to ACTUAL_ARRIVAL_TIME_STRING: ${timePart}`);
                return <Text>{timePart}</Text>;
              }
            }
            
            if (record.A_ARR !== undefined && record.A_ARR !== null) {
              minutes = Number(record.A_ARR);
              console.log(`Falling back to A_ARR: ${record.A_ARR} → ${minutes}`);
            } else if (record.ACTUAL_ARRIVAL_TIME) {
              minutes = parseTimeString(record.ACTUAL_ARRIVAL_TIME);
              console.log(`Falling back to ACTUAL_ARRIVAL_TIME: ${record.ACTUAL_ARRIVAL_TIME} → ${minutes}`);
            }
          }
          
          if (minutes === null || isNaN(minutes)) {
            return <Text>-</Text>;
          }
          
          const timeString = minutesToTimeString(minutes);
          console.log(`Time conversion: ${minutes} → ${timeString}`);
          return <Text>{timeString}</Text>;
        } catch (error) {
          console.error('Error rendering actual time:', error);
          return <Text>-</Text>;
        }
      }
    },
    {
      title: 'Departure Variance',
      key: 'variance',
      width: 120,
      render: (_, record) => {
        // Check for variance in all possible field locations
        let variance = null;
        
        try {
          // First try pre-calculated departure variance fields
          if (record.DEPARTURE_TIME_VARIANCE !== undefined && record.DEPARTURE_TIME_VARIANCE !== null) {
            variance = record.DEPARTURE_TIME_VARIANCE;
          } 
          // Then try to calculate from compact departure time fields
          else if (record.A_DEP !== undefined && record.S_DEP !== undefined) {
            variance = record.A_DEP - record.S_DEP;
          }
          // Then try to calculate from departure date-time strings
          else if (record.ACTUAL_DEPARTURE_TIME && record.SCHEDULED_DEPARTURE_TIME) {
            const actualMinutes = parseTimeString(record.ACTUAL_DEPARTURE_TIME);
            const scheduledMinutes = parseTimeString(record.SCHEDULED_DEPARTURE_TIME);
            
            if (actualMinutes !== null && scheduledMinutes !== null) {
              variance = actualMinutes - scheduledMinutes;
            }
          }
          
          // Fall back to arrival variance only if departure variance is not available
          if (variance === null || variance === undefined || isNaN(Number(variance))) {
            if (record.ARRIVAL_TIME_VARIANCE !== undefined && record.ARRIVAL_TIME_VARIANCE !== null) {
              variance = record.ARRIVAL_TIME_VARIANCE;
            }
            else if (record.A_ARR !== undefined && record.S_ARR !== undefined) {
              variance = record.A_ARR - record.S_ARR;
            }
            else if (record.ACTUAL_ARRIVAL_TIME && record.SCHEDULED_ARRIVAL_TIME) {
              const actualMinutes = parseTimeString(record.ACTUAL_ARRIVAL_TIME);
              const scheduledMinutes = parseTimeString(record.SCHEDULED_ARRIVAL_TIME);
              
              if (actualMinutes !== null && scheduledMinutes !== null) {
                variance = actualMinutes - scheduledMinutes;
              }
            }
          }
          
          // If we couldn't find or calculate a variance, return a dash
          if (variance === null || variance === undefined || isNaN(Number(variance))) {
            return <Text>-</Text>;
          }
          
          // Apply styling based on variance
          let color = 'green';
          if (variance <= OTR_THRESHOLDS.early) color = 'red';
          if (variance >= OTR_THRESHOLDS.late) color = 'blue';
          
          try {
            const formattedVariance = variance > 0 ? 
              `+${toHrsMinsSecs(Number(variance))}` : toHrsMinsSecs(Number(variance));
            return <Text style={{ color }}>{formattedVariance}</Text>;
          } catch (error) {
            console.warn('Error formatting variance:', variance, error);
            return <Text>-</Text>;
          }
        } catch (err) {
          console.error('Error calculating variance:', err);
          return <Text>-</Text>;
        }
      }
    }
  ];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px' }}>
            <Text>Loading trip data...</Text>
          </div>
        </div>
      </Card>
    );
  }

  if (!tripData || stopRecords.length === 0) {
    return (
      <Card>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={onClose} style={{ marginBottom: '20px' }}>
          Back to Trip List
        </Button>
        <Empty 
          description={`No data found for trip ${route} ${tripStartTimes[tripId]?.time || tripId}${dateId ? ` on date ${dateId}` : ''}`} 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      </Card>
    );
  }

  // Render the detail view
  return (
    <div>
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col xs={24} md={8}>
            <Space>
              <Button type="link" icon={<ArrowLeftOutlined />} onClick={onClose} style={{ paddingLeft: 0 }} />
              <Title level={4} style={{ margin: 0 }}>{`${route} ${tripStartTimes[tripId]?.time} Details`}</Title>
            </Space>
          </Col>
          
          <Col xs={24} md={16}>
            <Row gutter={[16, 16]} justify="end">
              <Col xs={12} sm={6} md={6} lg={3}>
                <Statistic 
                  title="Route" 
                  value={tripData?.route || '-'} 
                  prefix={<CarOutlined />}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <Statistic 
                  title="Driver" 
                  value={tripData?.driverId || 'Unknown'} 
                  valueStyle={{ fontSize: '16px' }}
                  formatter={(value) => value || 'Unknown'}
                />
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <Statistic 
                  title="Depot" 
                  value={tripData?.depot || 'Unknown'} 
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <Statistic
                  title="On-Time %"
                  value={tripStats && tripStats.onTimePercentage !== null ? tripStats.onTimePercentage.toFixed(1) : '0.0'}
                  suffix="%"
                  valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <Statistic
                  title="Early %"
                  value={tripStats && tripStats.earlyPercentage !== null ? tripStats.earlyPercentage.toFixed(1) : '0.0'}
                  suffix="%"
                  valueStyle={{ color: '#f5222d', fontSize: '16px' }}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <Statistic
                  title="Late %"
                  value={tripStats && tripStats.latePercentage !== null ? tripStats.latePercentage.toFixed(1) : '0.0'}
                  suffix="%"
                  valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <Statistic
                  title="Avg Variance"
                  value={tripStats && tripStats.averageVariance !== null ? toHrsMinsSecs(tripStats.averageVariance) : '0.0'}
                  prefix={<WarningOutlined />}
                  valueStyle={{ 
                    fontSize: '16px',
                    color: !tripStats || tripStats.averageVariance === null ? 
                      '#999999' :
                      Math.abs(tripStats.averageVariance) <= 2 
                        ? '#52c41a' // green - on time
                        : tripStats.averageVariance < 0 
                          ? '#f5222d' // red - early 
                          : '#1890ff' // blue - late
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
      
      <Card style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ margin: '0 0 16px 0' }}>Variance Chart</Title>
        <OTRTripChart data={chartData} />
      </Card>
      
      <Card>
        <Title level={5} style={{ margin: '0 0 16px 0' }}>Trip Details</Title>
        <Table
          columns={columns}
          dataSource={stopRecords}
          rowKey={(record, index) => `${record.TSN || record.TIME_RECORD_SEQUENCE || index}`}
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default OTRTripDetailView; 